import { computed, inject, Injectable, Injector, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { DateTime } from 'luxon';
import { EntityOptions } from '../../../shared/store/entity-state.feature';
import { SCORECARD_STATE } from './models/scorecard-state.enum';
import { ScorecardV2, ScorecardV2Badge } from './models/scorecard-v2.model';
import { ScorecardV2Store } from './scorecard-v2.store';

/** True while the scorecard end date is still in the future. */
function isNotExpired(scorecard: ScorecardV2): boolean {
  return DateTime.utc() < DateTime.fromISO(scorecard.EndDate, { zone: 'utc' });
}

/**
 * Query facade for the ScorecardV2Store.
 *
 * Components and services depend on this query rather than the store directly,
 * keeping the store an implementation detail.
 *
 * Naming convention:
 * - `get*` returns a Signal, use in templates or computed signals that need to react to changes
 * - `select*` returns an Observable, use in components or services that cannot consume signals directly
 */
@Injectable({ providedIn: 'root' })
export class ScorecardV2Query {
  private scorecardStore = inject(ScorecardV2Store);
  /** Needed so `toObservable` can bridge store signals outside an injection context. */
  private injector = inject(Injector);

  /** Signal, true once the scorecards have been loaded. */
  readonly isLoaded = this.scorecardStore.isLoaded;

  /** Signal, true while an API request is in flight. */
  readonly isLoading = this.scorecardStore.isLoading;

  /** Signal of all badges from the `scorecard/v2` response, in API order. */
  readonly badges: Signal<ScorecardV2Badge[]> = this.scorecardStore.badges;

  /** Returns a computed signal of all scorecards, sorted by Ordinal unless overridden. */
  getScorecards(options: EntityOptions = {}) {
    return this.scorecardStore.getAll(options);
  }

  /** Returns a computed signal for a single scorecard by id. */
  getScorecardById(id: number) {
    return this.scorecardStore.getEntity(id);
  }

  /**
   * Returns a computed signal of the favourited scorecards that have not expired.
   * Uses the `Favourite` flag rather than `State`, because State is evaluated in priority order
   * and a favourite can also be ComingSoon or ViewableOnly.
   */
  getFavourites(): Signal<ScorecardV2[]> {
    return this.scorecardStore.getAll({ filterBy: (scorecard: ScorecardV2) => scorecard.Favourite && isNotExpired(scorecard) });
  }

  /** Returns a computed signal of the scorecards in any of the given display states. */
  getScorecardsByState(states: SCORECARD_STATE[], options: EntityOptions = {}): Signal<ScorecardV2[]> {
    return this.scorecardStore.getAll({
      sortBy: options.sortBy,
      filterBy: (scorecard: ScorecardV2) => states.includes(scorecard.State),
    });
  }

  /** Returns a computed signal of the ids of the completed scorecards. */
  getCompletedScorecardIds(): Signal<number[]> {
    return computed(() =>
      this.scorecardStore
        .entities()
        .filter((scorecard) => scorecard.Completed)
        .map((scorecard) => scorecard.ScorecardId)
    );
  }

  /** True if any scorecard in the store is a child of the given scorecard. */
  hasLinkedScorecards(scorecardId: number): boolean {
    return this.scorecardStore.entities().some((scorecard) => scorecard.ParentId === scorecardId);
  }

  /** Returns an observable stream of all scorecards. @see getScorecards */
  selectScorecards(options: EntityOptions = {}): Observable<ScorecardV2[]> {
    return this.scorecardStore.selectAll(options);
  }

  /** Returns an observable stream of all badges from the `scorecard/v2` response. @see badges */
  selectBadges(): Observable<ScorecardV2Badge[]> {
    return toObservable(this.badges, { injector: this.injector });
  }

  /** Returns an observable stream of a single scorecard by id. */
  selectScorecardById(id: number): Observable<ScorecardV2 | undefined> {
    return this.scorecardStore.selectEntity(id);
  }

  /** Returns an observable stream of the favourited scorecards. @see getFavourites */
  selectFavourites(): Observable<ScorecardV2[]> {
    return toObservable(this.getFavourites(), { injector: this.injector });
  }

  /** Returns an observable stream of the scorecards in any of the given display states. @see getScorecardsByState */
  selectScorecardsByState(states: SCORECARD_STATE[], options: EntityOptions = {}): Observable<ScorecardV2[]> {
    return toObservable(this.getScorecardsByState(states, options), { injector: this.injector });
  }

  /** Returns an observable stream of the loading state. */
  selectLoading(): Observable<boolean> {
    return this.scorecardStore.selectLoading();
  }
}
